import React from 'react'
import { Filter, Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DateRangeModal } from '@/components/ui/DateRangeModal'
import type { DateRange } from '@/types'

interface MobileFloatingFilterProps {
  dateRange: DateRange
  onDateChange: (type: 'from' | 'to', value: string) => void
  onApply?: () => void
  title?: string
  description?: string
}

export function MobileFloatingFilter({
  dateRange,
  onDateChange,
  onApply,
  title = "Filter Date Range",
  description = "Select the date range for your report"
}: MobileFloatingFilterProps) {
  const formatDisplayDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  }

  const displayText = dateRange.from.toDateString() === dateRange.to.toDateString()
    ? formatDisplayDate(dateRange.from)
    : `${formatDisplayDate(dateRange.from)} - ${formatDisplayDate(dateRange.to)}`

  const handleDateChange = (type: 'from' | 'to', value: string) => {
    onDateChange(type, value)
    // Note: No auto-apply here - only apply when user clicks Apply button
  }

  return (
    <>
      {/* Mobile Floating Filter Button - Only show on mobile */}
      <div className="fixed bottom-40 right-4 z-50 md:hidden">
        <div className="flex flex-col items-end gap-2">
          {/* Current filter display */}
          <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-muted-foreground border shadow-lg">
            <div className="flex items-center gap-2">
              <Calendar className="h-3 w-3" />
              <span>{displayText}</span>
            </div>
          </div>

          {/* Filter button - Using custom trigger */}
          <FloatingDateRangeModal
            dateRange={dateRange}
            onDateChange={handleDateChange}
            onApply={onApply}
            title={title}
            description={description}
          />
        </div>
      </div>
    </>
  )
}

// Custom floating button with modal
function FloatingDateRangeModal({
  dateRange,
  onDateChange,
  onApply,
  title,
  description
}: MobileFloatingFilterProps) {
  const [open, setOpen] = React.useState(false)

  return (
    <>
      <Button
        size="lg"
        onClick={() => setOpen(true)}
        className="h-14 w-14 rounded-full shadow-lg bg-yellow-500 text-black hover:bg-yellow-600 flex items-center justify-center border-2 border-white"
        aria-label="Filter by date range"
      >
        <Calendar
          className="w-6 h-6"
          style={{ color: 'black', stroke: 'black', fill: 'none' }}
        />
      </Button>

      <DateRangeModal
        dateRange={dateRange}
        onDateChange={(type, value) => {
          onDateChange(type, value)
        }}
        onApply={() => {
          if (onApply) {
            onApply()
          }
        }}
        title={title}
        description={description}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  )
}